import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code2, Filter, TrendingUp } from 'lucide-react';

const TechStack = ({ technologies }) => {
  const [filter, setFilter] = useState('all');
  const [animated, setAnimated] = useState(false);

  // Catégories uniques
  const categories = ['all', ...new Set(technologies.map(t => t.category))];

  // Technologies filtrées
  const filteredTech = filter === 'all' 
    ? technologies 
    : technologies.filter(t => t.category === filter);

  useEffect(() => {
    setAnimated(true);
    const timer = setTimeout(() => setAnimated(false), 500);
    return () => clearTimeout(timer);
  }, [filter]);

  // Animation pour l'apparition en cascade
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 20 },
    show: { opacity: 1, scale: 1, y: 0 }
  };

  return (
    <section id="stack" className="py-16 section-spacing">
      <div className="flex items-center gap-3 mb-8">
        <div className="bg-green-600/20 p-3 rounded-full">
          <TrendingUp className="text-green-400" size={28} />
        </div>
        <div>
          <h2 className="text-3xl md:text-4xl font-bold">
            <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-green-400 bg-clip-text text-transparent">
              Stack technique
            </span>
          </h2>
          <div className="text-xs font-mono text-gray-500">$ skills --latest</div>
        </div>
      </div>

      {/* Filtres style terminal/IDE */}
      <div className="mb-8 flex flex-wrap items-center gap-3 font-mono">
        <Filter size={18} className="text-green-500" />
        <span className="text-gray-400">$ filter --category</span>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                filter === cat
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-600/30'
                  : 'bg-dark-card text-gray-400 hover:text-green-400 border border-dark-border'
              }`}
            >
              {cat === 'all' ? '~/all' : `~/ ${cat}`}
            </button>
          ))}
        </div>
      </div>

      {/* Grille de technologies dynamique */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
      >
        {filteredTech.map((tech, index) => (
          <motion.div
            key={`${tech.name}-${index}`}
            variants={itemVariants}
            whileHover={{ scale: 1.05, y: -5 }}
            className="group relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-dark-card border border-dark-border rounded-xl p-4 hover:border-green-500/50 transition-all duration-300 overflow-hidden">
              {/* Ligne de code style terminal */}
              <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-green-500 via-emerald-500 to-green-500 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-700" />
              
              <div className="flex flex-col items-center text-center">
                <span className="text-3xl mb-2">{tech.icon}</span>
                <span className="font-mono text-sm text-gray-300 group-hover:text-green-400 transition-colors">
                  {tech.name}
                </span>
                <span className="text-xs text-gray-500 mt-1 font-mono">
                  {tech.category}
                </span>
                {/* Niveau en vert */}
                {/* <span className="text-xs text-green-500 mt-1">
                  {tech.level}%
                </span> */}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Compteur style terminal avec vert */}
      <div className="mt-6 font-mono text-sm text-gray-500 border-t border-dark-border pt-4 flex items-center justify-between">
        <div>
          <span className="text-green-400">$</span> echo "Affichage de {filteredTech.length} technologies sur {technologies.length}"
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
          <span className="text-xs text-green-400">À jour 2026</span>
        </div>
      </div>
    </section>
  );
};

export default TechStack;